import React, { Fragment, memo } from 'react';
import type { FC } from 'react';
import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import FadeIn from '@atlaskit/motion/fade-in';
import StaggeredEntrance from '@atlaskit/motion/staggered-entrance';
import { reduceMotionAsPerUserPreference } from '@atlaskit/motion/accessibility';

const skeletonWrapperStyle = css({
	marginBottom: token('space.200'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage
	...reduceMotionAsPerUserPreference,
});

const skeletonBodyStyle = css({
	background: token('color.skeleton'),
	height: '197px',
	width: '100%',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.large', '8px'),
});

type SkeletonProps = {
	useEntranceAnimation?: boolean;
};

export const FeedCardSkeleton: FC<SkeletonProps> = ({ useEntranceAnimation }) => {
	if (useEntranceAnimation) {
		return (
			<FadeIn entranceDirection="bottom">
				{(animationProps) => (
					<div css={skeletonWrapperStyle} {...animationProps} data-testid="feed-card-skeleton">
						<div data-testid="skeleton-body" css={skeletonBodyStyle} />
					</div>
				)}
			</FadeIn>
		);
	}

	return (
		<div css={skeletonWrapperStyle} data-testid="feed-card-skeleton">
			<div data-testid="skeleton-body" css={skeletonBodyStyle} />
		</div>
	);
};

export const LoadingSkeleton: FC<SkeletonProps> = memo(
	({ useEntranceAnimation }) => {
		const Wrapper = useEntranceAnimation ? StaggeredEntrance : Fragment;
		return (
			<Wrapper columns={1}>
				<FeedCardSkeleton useEntranceAnimation={useEntranceAnimation} />
				<FeedCardSkeleton useEntranceAnimation={useEntranceAnimation} />
			</Wrapper>
		);
	},
	() => true,
);
